import type { MediaVariantKey, PublicImage } from '@ahora-patagonia/contracts';

export interface FuenteImagen {
  src: string;
  srcSet?: string;
}

/**
 * Arma `src`/`srcset` desde `Media.variants` para una clave: los tres recortes, o `full` cuando
 * la imagen no se puede recortar (la foto del cuerpo de una nota). Si la media no tiene esa
 * variante (subida antes del worker, o el worker aún no corrió) cae a la URL original: misma
 * caja, mismo layout, solo más pesada — es el fallback del contrato, no un error.
 */
export function srcsetDe(imagen: PublicImage | null, ratio: MediaVariantKey): FuenteImagen | null {
  if (!imagen) return null;

  const variantes = (imagen.variants?.[ratio] ?? []).filter(
    (v): v is typeof v & { url: string } => v.url !== null,
  );
  if (variantes.length > 0) {
    const mayor = variantes[variantes.length - 1]!;
    return {
      src: mayor.url,
      srcSet: variantes.map((v) => `${v.url} ${v.width}w`).join(', '),
    };
  }

  return imagen.url ? { src: imagen.url } : null;
}

/**
 * `sizes` por plantilla (§4 del handoff: srcset con anchos y sizes por plantilla).
 * Los nombres son los lugares donde se dibuja una imagen, no las plantillas A–E.
 */
export const SIZES = {
  /** Posición 1 de la portada: dos tercios del ancho en escritorio, todo en teléfono. */
  portada1: '(min-width: 1200px) 60vw, 100vw',
  /**
   * Maqueta B: la nota única corre a ancho completo del contenido, **por encima de la columna
   * lateral**, así que en escritorio también es el ancho de la ventana menos los gutters. No hay
   * corte que declarar: es 100vw en los tres tamaños.
   */
  portadaB: '100vw',
  /** Maqueta C: las dos del frente se reparten el ancho completo en mitades; en móvil apilan. */
  portadaC: '(min-width: 1200px) 50vw, 100vw',
  /** Tarjeta de tira (plantilla B): 4 por fila en escritorio, 2 en tablet. */
  tarjeta: '(min-width: 1200px) 22vw, (min-width: 600px) 45vw, 100vw',
  /**
   * Tarjeta de cuadrícula (plantilla D): la misma tarjeta que la tira, pero **3 por fila** en
   * escritorio en vez de 4. El valor está **medido**, no calculado sobre el ancho de la ventana: en
   * 1440 el cuerpo de la home mide unos 1110 px (el resto se lo llevan la columna lateral de 300 y
   * los gutters), así que un tercio son ~330 px, que es 23vw y no el 33vw que sugeriría "3 de 3
   * columnas". Se pide 24vw para no quedar corto por un pixel.
   *
   * Importa más que en otras plantillas: es el bloque que puede llegar a 60 fotos, y sobrepedir acá
   * se paga sesenta veces.
   *
   * En tablet y teléfono la grilla es igual que la de la tira, y el valor coincide.
   */
  tarjetaCuadricula: '(min-width: 1200px) 24vw, (min-width: 600px) 45vw, 100vw',
  /** Miniatura de lista (plantilla C, listados de categoría y búsqueda). */
  miniatura: '(min-width: 600px) 132px, 96px',
  /** Foto dentro del cuerpo de la nota: el ancho de la columna de lectura. */
  cuerpo: '(min-width: 1200px) 880px, 100vw',
} as const;
